<template>
    <div>
        <card title="Merge Prescribers">

            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title">Merge Prescribers</h1>

                    <div>
                        <HelpPopup label="Merge Info">
                            <p>
                                All records from source prescriber will be associated to the target prescriber
                                and the source prescriber record will be deleted from the database.
                            </p>
                            <br>
                            <p>
                                The data to be merged is:
                            </p>
                            <ul>
                                <li>Prescriber</li>
                                <li>Follow-Up Cases</li>
                                <li>Notes</li>
                                <li>Prescriptions</li>
                                <li>Prescriber Contacts</li>
                            </ul>
                        </HelpPopup>
                    </div>
                </div>
            </template>

            <b-row align-v="end">
                <b-col>
                    <SearchComponent v-model="sourcePrescriber" :key="sourcePrescriber.id ?? 0" label="Source"
                        search-u-r-l="/Prescriber" clearable />
                </b-col>
                <b-col cols="1" class="text-center">
                    <b-button size="sm" class="swap" @click="swap" variant="outline-default" v-b-tooltip.hover
                        title="Swap Source and Target">
                        <i class="fa fa-exchange" />
                    </b-button>
                </b-col>
                <b-col>
                    <SearchComponent v-model="targetPrescriber" :key="targetPrescriber.id ?? 0" label="Target"
                        search-u-r-l="/Prescriber" clearable />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <div v-if="sourcePrescriber && sourcePrescriber.id">
                        <b-form-row>
                            <b-col>
                                <AddressFormFields v-model="sourcePrescriber" disable-fields>
                                    <template #row-1>
                                        <b-col cols="4" offset="1">
                                            <fg-input v-model="sourcePrescriber.altNumber" label="Alternate Phone"
                                                disabled />
                                        </b-col>
                                    </template>
                                    <template #row-2>
                                        <b-col cols="4" offset="1">
                                            <fg-input v-model="sourcePrescriber.faxNumber" label="Fax" disabled />
                                        </b-col>
                                    </template>
                                </AddressFormFields>
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="7">
                                <fg-input v-model="sourcePrescriber.emailAddress" label="Email" type="email" disabled />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <fg-input v-model="sourcePrescriber.deaNumber" label="DEA Number" disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="sourcePrescriber.npiNumber" label="NPI Number" disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="sourcePrescriber.stateLicenseNumber" label="State License Number"
                                    disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="sourcePrescriber.sureScriptsId" label="Sure Scripts ID" disabled />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="12">
                                <fg-input label="Alert">
                                    <textarea v-model="sourcePrescriber.alert" class="form-control" disabled />
                                </fg-input>
                            </b-col>
                            <b-col>
                                <b-checkbox v-model="sourcePrescriber.allowsPa" disabled>Allows Pa</b-checkbox>
                                <b-checkbox v-model="sourcePrescriber.reminderOptOut" disabled>Fax Reminder Opt
                                    Out</b-checkbox>
                            </b-col>
                        </b-form-row>
                        <hr>
                        <b-row>
                            <b-col>
                                <NotesForm :entityId="sourcePrescriber.id" :entityType="doctorEntityType"
                                    @saved="refreshNotesList" />
                                <NotesList ref="sourceNotesList" :entityId="sourcePrescriber.id"
                                    :entityType="doctorEntityType" :pageSize="5" />
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
                <b-col>
                    <div v-if="targetPrescriber && targetPrescriber.id">
                        <b-form-row>
                            <b-col>
                                <AddressFormFields v-model="targetPrescriber" disable-fields>
                                    <template #row-1>
                                        <b-col cols="4" offset="1">
                                            <fg-input v-model="targetPrescriber.altNumber" label="Alternate Phone"
                                                disabled />
                                        </b-col>
                                    </template>
                                    <template #row-2>
                                        <b-col cols="4" offset="1">
                                            <fg-input v-model="targetPrescriber.faxNumber" label="Fax" disabled />
                                        </b-col>
                                    </template>
                                </AddressFormFields>
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="7">
                                <fg-input v-model="targetPrescriber.emailAddress" label="Email" type="email" disabled />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <fg-input v-model="targetPrescriber.deaNumber" label="DEA Number" disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="targetPrescriber.npiNumber" label="NPI Number" disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="targetPrescriber.stateLicenseNumber" label="State License Number"
                                    disabled />
                            </b-col>
                            <b-col>
                                <fg-input v-model="targetPrescriber.sureScriptsId" label="Sure Scripts ID" disabled />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="12">
                                <fg-input label="Alert">
                                    <textarea v-model="targetPrescriber.alert" class="form-control" disabled />
                                </fg-input>
                            </b-col>
                            <b-col>
                                <b-checkbox v-model="targetPrescriber.allowsPa" disabled>Allows Pa</b-checkbox>
                                <b-checkbox v-model="targetPrescriber.reminderOptOut" disabled>Fax Reminder Opt
                                    Out</b-checkbox>
                            </b-col>
                        </b-form-row>
                        <hr>
                        <b-row>
                            <b-col>
                                <NotesForm :entityId="targetPrescriber.id" :entityType="doctorEntityType"
                                    @saved="refreshNotesList" />
                                <NotesList ref="targetNotesList" :entityId="targetPrescriber.id"
                                    :entityType="doctorEntityType" :pageSize="5" />
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
            </b-row>
            <BottomPagerBar hide-pager>
                <template #right>
                    <b-button v-if="userCanMerge" variant="success" @click="submit" :disabled="disableSubmit">
                        Merge Prescribers
                    </b-button>
                    <!-- <b-button v-else
                              variant="outline-primary">
                        Add to Merge Queue
                    </b-button> -->
                </template>
            </BottomPagerBar>
        </card>

    </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { Prescriber } from "@/models/Prescriber";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import DoctorFormComponent from "@/components/Doctor/DoctorFormComponent.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import NotesList from "@/components/Note/NotesList.vue";
import AddressFormFields from "@/components/AddressFormFields.vue";
import { EntityType } from "@/models/EntityType";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import HelpPopup from "@/components/HelpPopup.vue";

@Component({
    name: "DoctorMergePage",
    components: { AddressFormFields, NotesList, NotesForm, DoctorFormComponent, SearchComponent, BottomPagerBar, HelpPopup },
})
export default class DoctorMergePage extends Vue {
    protected sourcePrescriber: Prescriber = new Prescriber();
    protected targetPrescriber: Prescriber = new Prescriber();

    private doctorEntityType = EntityType.Prescriber;

    get disableSubmit(): boolean {
        return !this.sourcePrescriber?.id || !this.targetPrescriber?.id;
    }

    get userCanMerge() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Maintenance/Doctor".toLowerCase());
    }

    created() {
        console.log("%c Created DoctorMergePage", "color: green");
    }

    beforeDestroy() {
        console.log('%c Destroying DoctorMergePage', 'color: red');
    }

    swap() {
        [this.sourcePrescriber, this.targetPrescriber] = [this.targetPrescriber, this.sourcePrescriber];
    }

    async submit() {
        try {
            if (this.sourcePrescriber.id === this.targetPrescriber.id) {
                this.$notification(NotificationOptions.error("Source and target prescriber can't be the same record"));
                return;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm("This action is not reversible. Are you sure?", {
                title: 'Confirm Merge',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!confirmed) return;

            const body = { source: this.sourcePrescriber, target: this.targetPrescriber };

            const resp = await axios.post<Prescriber>("/api/Prescriber/Merge", body);
            this.sourcePrescriber = new Prescriber();
            this.targetPrescriber = new Prescriber(resp.data);

            this.$notification(NotificationOptions.success("Successfully completed merge."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    refreshNotesList() {
        if (this.$refs.sourceNotesList instanceof NotesList) this.$refs.sourceNotesList.loadNotes();
        if (this.$refs.targetNotesList instanceof NotesList) this.$refs.targetNotesList.loadNotes();
    }

}
</script>

<style scoped lang="scss">
.btn.swap {
    font-size: 1.4em;
    border-radius: 30px;
}
</style>
